import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { requestPersistentStorage, reCacheAppShell } from '@/utils/pwaUtils';

export interface ModuleCacheStatus {
  cached: boolean;
  cachedAt: string | null;
}

interface OfflineStore {
  moduleStatus: Record<string, ModuleCacheStatus>;
  isOnline: boolean;
  isSupported: boolean;
  isCaching: string | null;
  cacheSection: (sectionId: string) => Promise<void>;
  removeSection: (sectionId: string) => Promise<void>;
  cacheAllSections: (sectionIds: string[]) => Promise<void>;
  setOnline: (online: boolean) => void;
  initialize: () => void;
}

const CACHE_NAME = 'emg-atlas-v1';

function getSectionUrls(sectionId: string): string[] {
  return [`/atlas/${sectionId}`, `/atlas/${sectionId}/`];
}

export const useOfflineStore = create<OfflineStore>()(
  persist(
    (set, get) => ({
      moduleStatus: {},
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      isSupported: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
      isCaching: null,

      initialize: () => {
        const isSupported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
        set({ isSupported, isOnline: navigator.onLine });

        window.addEventListener('online', () => set({ isOnline: true }));
        window.addEventListener('offline', () => set({ isOnline: false }));

        if (isSupported) {
          void requestPersistentStorage();
          void reCacheAppShell();
        }
      },

      cacheSection: async (sectionId: string) => {
        if (!get().isSupported) return;

        set({ isCaching: sectionId });

        try {
          const cache = await caches.open(CACHE_NAME);
          const urls = getSectionUrls(sectionId);

          for (const url of urls) {
            try {
              const response = await fetch(url);
              if (response.ok) {
                await cache.put(url, response);
              }
            } catch {
              console.warn(`[Offline] Could not cache: ${url}`);
            }
          }

          try {
            const rootResponse = await fetch('/atlas');
            if (rootResponse.ok) {
              await cache.put('/atlas', rootResponse);
            }
          } catch {
            // Workbox precache covers the app shell
          }

          set((state) => ({
            moduleStatus: {
              ...state.moduleStatus,
              [sectionId]: {
                cached: true,
                cachedAt: new Date().toISOString(),
              },
            },
            isCaching: null,
          }));
        } catch (error) {
          console.error(`[Offline] Failed to cache section ${sectionId}:`, error);
          set({ isCaching: null });
        }
      },

      removeSection: async (sectionId: string) => {
        try {
          const cache = await caches.open(CACHE_NAME);
          const urls = getSectionUrls(sectionId);

          for (const url of urls) {
            await cache.delete(url);
          }

          set((state) => ({
            moduleStatus: {
              ...state.moduleStatus,
              [sectionId]: { cached: false, cachedAt: null },
            },
          }));
        } catch (error) {
          console.error(`[Offline] Failed to remove section ${sectionId}:`, error);
        }
      },

      cacheAllSections: async (sectionIds: string[]) => {
        const { cacheSection } = get();
        for (const id of sectionIds) {
          await cacheSection(id);
        }
      },

      setOnline: (online: boolean) => set({ isOnline: online }),
    }),
    {
      name: 'offline-storage',
      partialize: (state) => ({
        moduleStatus: state.moduleStatus,
      }),
    },
  ),
);
